import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api-client';
import type { LayoutDocument } from '../lib/form-layout/types';
import type { EntryFormFieldName } from '../lib/form-layout/entry-fields';

export type EntryLayoutDocument = LayoutDocument<EntryFormFieldName>;

export interface ResolvedLayout {
  layout: EntryLayoutDocument;
  scope: 'global' | 'project' | 'default';
  resolved_from: 'project' | 'global' | 'none';
  source_id: string | null;
  updated_at: string | null;
  notes: string | null;
}

export const entryFormLayoutKeys = {
  all: ['entry-form-layout'] as const,
  active: (projectId: string | null) =>
    [...entryFormLayoutKeys.all, 'active', projectId] as const,
  adminActive: (scope: 'global' | 'project', projectId: string | null) =>
    [...entryFormLayoutKeys.all, 'admin', 'active', scope, projectId] as const,
};

/** Active layout for the entry-form drawer. Project-scoped if projectId is set. */
export function useEntryFormLayout(projectId: string | null) {
  return useQuery({
    queryKey: entryFormLayoutKeys.active(projectId),
    staleTime: 60_000,
    queryFn: () =>
      api.get<ResolvedLayout>(
        '/entry-form-layout',
        projectId ? { project_id: projectId } : undefined,
      ),
  });
}

/** Editor's "current draft starting point" for a given (scope, project_id). */
export function useAdminActiveEntryLayout(scope: 'global' | 'project', projectId: string | null) {
  return useQuery({
    queryKey: entryFormLayoutKeys.adminActive(scope, projectId),
    queryFn: () => {
      const q: Record<string, string> = { scope };
      if (scope === 'project' && projectId) q.project_id = projectId;
      return api.get<ResolvedLayout>('/admin/entry-form-layout/active', q);
    },
    enabled: scope === 'global' || projectId !== null,
  });
}

export interface UpdateLayoutInput {
  scope: 'global' | 'project';
  project_id?: string | null;
  layout: EntryLayoutDocument;
  notes?: string | null;
}

export function useUpdateEntryFormLayout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateLayoutInput) =>
      api.put<ResolvedLayout>('/admin/entry-form-layout', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: entryFormLayoutKeys.all }),
  });
}
